import { inject, Injectable } from "@angular/core";
import { Capacitor } from "@capacitor/core";
import { GetResult, Preferences } from "@capacitor/preferences";
import { Storage } from "@ionic/storage-angular";

import { assert, assertExists } from "./util";

export class ConfigNode {
  name: string;
  type: "boolean" | "number" | "string";
  defaultVal?: boolean | number | string;

  constructor(name: string, type: "boolean" | "number" | "string", defaultVal?: boolean | number | string) {
    assert(defaultVal == null || typeof defaultVal == type);

    this.name = name;
    this.type = type;
    this.defaultVal = defaultVal;
  }
}

function nodeFrom(base: {
  name: string;
  type: "boolean" | "number" | "string";
  defaultVal: boolean | number | string;
}) {
  return new ConfigNode(base.name, base.type, base.defaultVal);
}

type ConfigDef = ConfigNode[];

export const AppConfigDef: ConfigDef = [
  nodeFrom({ name: "forceHighContrast", type: "boolean", defaultVal: false }),
  nodeFrom({ name: "forceNoMotion", type: "boolean", defaultVal: false }),
  nodeFrom({ name: "language", type: "string", defaultVal: "en" }),
  nodeFrom({ name: "sessionDetails", type: "string", defaultVal: "" }),
];

export class Config {
  def: ConfigDef;

  storage?: Storage | null;

  store = new Map<string, boolean | number | string>();

  constructor(def: ConfigDef) {
    this.def = def;
  }

  async getNative(key: string): Promise<string | null> {
    let result: string | null = null;

    if (Capacitor.getPlatform() === "web") {
      result = (await this.storage?.get(key)) as string | null;
    } else {
      const val: GetResult = await Preferences.get({ key: key });
      result = val.value;
    }

    console.log("Retrieve preferences key", key, "to", result);

    if (result === null) {
      return null;
    }

    return result;
  }
  async setNative(key: string, value: boolean | number | string) {
    console.log("Set preferences key", key, "to", value);

    if (Capacitor.getPlatform() === "web") {
      assertExists(this.storage);
      await this.storage.set(key, value);
    } else {
      await Preferences.set({ key: key, value: value.toString() });
    }
  }

  get(key: string): string | undefined {
    return this.store.get(key) as string | undefined;
  }
  async set(key: string, value: boolean | number | string) {
    this.store.set(key, value);
    await this.setNative(key, value);
  }
  async delete(key: string) {
    if (Capacitor.getPlatform() === "web") {
      await this.storage?.remove(key);
    } else {
      await Preferences.remove({ key: key });
    }
  }

  async init(storage: Storage | null) {
    if (Capacitor.getPlatform() === "web") {
      this.storage = storage;
    }

    for (const i of this.def) {
      let value = i.defaultVal;
      assertExists(value);

      const storedVal = await this.getNative(i.name);

      if (storedVal === null) {
        await this.setNative(i.name, value.toString());
      } else {
        value = storedVal;
      }

      this.store.set(i.name, value.toString());
    }
  }

  async reset() {
    if (Capacitor.getPlatform() === "web") {
      if (!this.storage) {
        return;
      }
      const keys = await this.storage.keys();
      for (const i of keys) {
        await this.storage.remove(i);
      }
    } else {
      await Preferences.clear();
    }
  }
}

@Injectable()
export class ConfigService {
  storage: null | Storage = Capacitor.getPlatform() === "web" ? inject(Storage) : null;

  config = new Config(AppConfigDef);

  initComplete = false;

  async init() {
    if (this.initComplete) {
      return;
    }

    if (Capacitor.getPlatform() === "web") {
      assertExists(this.storage);
      await this.storage.create();
    }

    await this.config.init(this.storage);

    this.initComplete = true;
  }

  // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-parameters
  get<T>(key: string): T | null {
    const val = this.config.get(key);

    if (val === undefined) {
      return null;
    }

    const type = this.config.def.find((v) => v.name === key)?.type;

    if (type === "boolean") {
      return (val === "true") as T;
    } else if (type === "number") {
      return Number.parseInt(val) as T;
    } else if (type === "string") {
      return val as T;
    } else {
      throw new Error("Invalid config key type");
    }
  }

  async set(key: string, value: boolean | number | string) {
    await this.config.set(key, value.toString());
  }

  async delete(key: string) {
    await this.config.delete(key);
  }

  async reset() {
    await this.config.reset();
  }
}
